import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useAuthState } from "react-firebase-hooks/auth";
import { auth } from "../firebase";
import Window from "../components/Window";
import Icon, { IconName, IconTile } from "../components/Icon";
import { ReportDialog } from "../components/SafetyBits";

const rules = [
  {
    icon: "heart" as IconName,
    title: "Lead with compassion",
    text: "Speak and listen from the heart. Our words matter. This is a space to uplift one another and practice mutual respect.",
  },
  {
    icon: "lock" as IconName,
    title: "18+ only",
    text: "This is an adult-only platform. Anyone under 18 will be removed to keep the community safe.",
  },
  {
    icon: "check" as IconName,
    title: "Honor consent & boundaries",
    text: "Every connection is rooted in trust. Keep the board, rooms, and the PenPal Post friendly and non-explicit, with content warnings for heavy topics. In private letters, between two adults who both want it, that's your business. Don't send explicit content unless it's explicitly welcomed, and stop the moment it isn't. Respect how others want to engage.",
  },
  {
    icon: "eye" as IconName,
    title: "Protect privacy",
    text: "Don't share personal addresses, phone numbers, or private info. Build trust slowly, with care.",
  },
  {
    icon: "user" as IconName,
    title: "Come as you are, authentically",
    text: "This isn't the place for catfishing or fake identities. We thrive when we're real with each other.",
  },
  {
    icon: "warning" as IconName,
    title: "Report harm, not just conflict",
    text: null,
  },
  {
    icon: "flag" as IconName,
    title: "A queer, trans, and disabled-centered space",
    text: "This site exists because many systems don't. We believe in radical inclusion and prioritize those often left out.",
  },
  {
    icon: "zap" as IconName,
    title: "Anti-racist, anti-fascist, and trauma-informed",
    text: "No TERFs, no white supremacy, no ableism. Accountability is an act of care, not punishment.",
  },
  {
    icon: "stars" as IconName,
    title: "Practice non-attachment to outcome",
    text: "Not every connection will blossom, and that's okay. Offer kindness without expectation.",
  },
];

export default function SafetyRules() {
  const [reporting, setReporting] = useState(false);
  const [user] = useAuthState(auth);
  return (
    <Window title="house_rules.txt" tone="tan">
      <h1 className="window-heading flex items-center gap-3">
        <IconTile name="shield" tone="violet" size={44} /> Safety &amp; Community Guidelines
      </h1>
      <p className="window-sub">
        These aren't just rules, they're values. We're co-creating a soft,
        fierce, affirming place together.
      </p>

      <ol className="flex flex-col gap-3">
        {rules.map((rule, i) => (
          <li key={rule.title} className="profile-box bg-white">
            <div className="profile-box-body flex gap-3">
              <span className="mt-0.5">
                <Icon name={rule.icon} size={24} tone={i % 2 ? "blue" : "pink"} pop />
              </span>
              <div>
                <h2 className="font-display text-lg font-semibold">
                  {i + 1}. {rule.title}
                </h2>
                <p className="text-sm">
                  {rule.text ??
                    "If someone is causing harm, tell us. This is a community-led space, and we take safety seriously."}
                </p>
                {!rule.text && (
                  <div className="mt-2 flex flex-col items-start gap-2">
                    {user ? (
                      <button type="button" className="btn btn-sm" onClick={() => setReporting(true)}>
                        <Icon name="flag" size={14} /> Report a problem
                      </button>
                    ) : (
                      <Link to="/signin" className="btn btn-sm">
                        <Icon name="flag" size={14} /> Sign in to report a problem
                      </Link>
                    )}
                    <p className="text-sm text-ink-soft">
                      Every report is read and reviewed by a real person on our
                      team, never just a bot. Our team can see who sent a report so
                      we can follow up, but the person you report is never told.
                      You can also email us at{" "}
                      <a href="mailto:2slgbtqiapenpal@gmail.com">2slgbtqiapenpal@gmail.com</a>.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </li>
        ))}
      </ol>

      <div className="divider mt-6" />
      <p className="mt-2 text-center font-hand text-xl">
        Thank you for being here. <Icon name="heart" tone="pink" size={18} />
      </p>
      {reporting && <ReportDialog target={{}} onClose={() => setReporting(false)} />}
    </Window>
  );
}
